import ArrowCircleDown from '@mui/icons-material/ArrowCircleDown';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';
import { useRef } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  LoadingSpinner,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';

interface ApprovedChipProps {
  hideDropdownArrow?: boolean;
  isLoading?: boolean;
}

export const ApprovedChip = ({ hideDropdownArrow, isLoading }: ApprovedChipProps) => {
  const theme = useTheme();

  return (
    <Chip
      color={'success'}
      disabled={isLoading}
      icon={
        hideDropdownArrow ? undefined : (
          <ArrowDropDown
            data-test-id="approved-chip-dropdown-arrow"
            sx={{ color: theme.palette.success.main, fontSize: '15px' }}
          />
        )
      }
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isLoading && <LoadingSpinner isVisible loadingIndicatorSize={12} />}
          {t('approvals.driver_day.approved')}
        </Box>
      }
      size="small"
      variant="outlined"
      sx={{
        '&.MuiChip-root': {
          '&.MuiChip-outlined': { flexDirection: 'row-reverse', borderRadius: 1 },
          '.MuiChip-label': {
            fontSize: '12px',
            fontWeight: 700,
            pr: hideDropdownArrow ? undefined : 0,
          },
          '.MuiChip-icon': { mx: 0 },
        },
      }}
    />
  );
};

const UnApprovedChip = ({ isLoading }: { isLoading?: boolean }) => {
  const theme = useTheme();

  return (
    <Chip
      color={'primary'}
      disabled={isLoading}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isLoading && <LoadingSpinner isVisible loadingIndicatorSize={12} />}
          {t('approvals.driver_day.not_approved')}
        </Box>
      }
      icon={
        <ArrowDropDown sx={{ color: theme.semantic.chipBrandText, fontSize: '15px' }} />
      }
      size="small"
      variant="outlined"
      sx={{
        '&.MuiChip-root': {
          '&.MuiChip-outlined': { flexDirection: 'row-reverse', borderRadius: 1 },
          '.MuiChip-label': {
            fontSize: '12px',
            fontWeight: 700,
          },
          '.MuiChip-icon': { mx: 0 },
        },
      }}
    />
  );
};

const ApprovalActionsMenu = ({
  approved,
  isLoading,
  onMenuItemClick,
}: {
  approved: boolean;
  isLoading?: boolean;
  onMenuItemClick?: () => void | Promise<void>;
}) => {
  const theme = useTheme();
  const menuHandler = useRef<MenuHandler>(null);

  return (
    <Menu
      ref={menuHandler}
      disableAutoFocusItem
      menuTrigger={
        <Box
          sx={{
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'inline-block',
            height: 'fit-content',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {approved ? (
            <ApprovedChip isLoading={isLoading} />
          ) : (
            <UnApprovedChip isLoading={isLoading} />
          )}
        </Box>
      }
    >
      <MenuItem
        onClick={async (event: React.MouseEvent) => {
          event.stopPropagation();

          if (onMenuItemClick && !isLoading) {
            await onMenuItemClick();
          }
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {isLoading ? (
            <LoadingSpinner
              isVisible
              loadingIndicatorSize={15}
              sx={{ flex: 0, minWidth: '15px' }}
            />
          ) : (
            <ArrowCircleDown
              sx={{
                color: onMenuItemClick
                  ? theme.brandV2.colors.treadBlack
                  : theme.brandV2.colors.treadGray6,
                fontSize: '15px',
                transform: 'rotate(90deg)',
              }}
            />
          )}

          <OverflowAwareText
            flex={1}
            sx={{
              color:
                onMenuItemClick && !isLoading
                  ? theme.brandV2.colors.treadBlack
                  : theme.brandV2.colors.treadGray6,
            }}
          >
            {approved
              ? t('approvals.driver_day.unapprove')
              : t('approvals.driver_day.approve')}
          </OverflowAwareText>

          <LoadingSpinner loadingIndicatorSize={15} sx={{ flex: 0, minWidth: '15px' }} />
        </Box>
      </MenuItem>
    </Menu>
  );
};

export default ApprovalActionsMenu;
